import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';

import { ConfirmModal } from '~/components/ConfirmModal';
import { SortableTable } from '~/components/core';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { CheckIcon, EyeIcon, PencilIcon, TrashIcon } from '~/components/icons';
import UpsertDocumentTypeDialog from '~/components/SystemConfiguration/DocumentTypes/UpsertDocumentTypeDialog';
import ViewDocumentTypeDialog from '~/components/SystemConfiguration/DocumentTypes/ViewDocumentTypeDialog';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { DocumentType } from '~/components/types/document-type';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { reportAxiosError, stringCmp } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

const SYSTEM_DOC_TYPE_DISABLE_TOOLTIP =
  'The system keeps creating documents of this type automatically, so it cannot be disabled';
// The same lock seen from the other side: a locked type that is currently off can still be
// switched on, but that is a one-way move, so the row says so before the admin makes it.
const SYSTEM_DOC_TYPE_ENABLE_IS_FINAL_TOOLTIP =
  'The system keeps creating documents of this type automatically — once enabled, it cannot be disabled again';

interface DocumentTypesTableProps {
  documentTypesDict: Record<string, DocumentType>;
  reloadDocumentTypes: () => void;
  searchTerm: string;
  selectedLobs: (string | number)[];
  showOnlyCustomTypes: boolean;
  showOnlyEnabled: boolean;
}

const ALL_LOBS_SENTINEL_ID = 0;
const ROWS_PER_PAGE = 10;

const DocumentTypesTable: React.FC<DocumentTypesTableProps> = ({
  documentTypesDict,
  reloadDocumentTypes,
  searchTerm,
  selectedLobs,
  showOnlyCustomTypes,
  showOnlyEnabled,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { lobConfigurationsDict } = useLobConfiguration();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const [documentTypeToEdit, setDocumentTypeToEdit] = useState<DocumentType | null>(null);
  const [documentTypeToView, setDocumentTypeToView] = useState<DocumentType | null>(null);
  const [documentTypeToDelete, setDocumentTypeToDelete] = useState<DocumentType | null>(null);
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<keyof DocumentType>('display_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchesSearch = (row: DocumentType) =>
    isEmpty(normalizedSearch) ||
    row.display_name.toLowerCase().includes(normalizedSearch) ||
    row?.key?.toLowerCase().includes(normalizedSearch);

  const matchesCustomTypesToggle = (row: DocumentType) => !showOnlyCustomTypes || !row?.type || row.type === 'custom';

  const matchesEnabledToggle = (row: DocumentType) => !showOnlyEnabled || row.is_enabled;

  const matchesLobs = (row: DocumentType) => {
    if (isEmpty(selectedLobs) || selectedLobs.includes(ALL_LOBS_SENTINEL_ID)) return true;
    if (row.is_all_lobs) return true;
    return (row.lobs ?? []).some((lob) => selectedLobs.includes(lob));
  };

  const filteredDocumentTypes = Object.values(documentTypesDict).filter(
    (row: DocumentType) =>
      matchesSearch(row) && matchesCustomTypesToggle(row) && matchesEnabledToggle(row) && matchesLobs(row)
  );

  // Sort the whole filtered list before slicing. SortableTable only ever receives the current
  // page, so leaving the sort to it sorts each page independently and the alphabet restarts on
  // page 2. Follows SortableTable's own comparator (core/Tables/SortableTable/index.jsx) — none of
  // the columns below is numeric or declares a specialCmpFunc, so its string branch is the only one
  // in play — with one deliberate difference: the two `last_updated_*` columns are `string | null`
  // and null is their default for a type nobody has edited, so stringifying them would sort those
  // rows under the literal text 'null', in among the names beginning with 'n'. They are the rows
  // rendered as '—', and they hold that place at the bottom in both directions.
  const sortedDocumentTypes = [...filteredDocumentTypes].sort((row1, row2) => {
    const value1 = row1[sortColumn];
    const value2 = row2[sortColumn];
    if (isNil(value1) && isNil(value2)) return 0;
    if (isNil(value1)) return 1;
    if (isNil(value2)) return -1;

    const comparison = stringCmp(String(value1), String(value2));
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const pageCount = Math.max(1, Math.ceil(sortedDocumentTypes.length / ROWS_PER_PAGE));
  const paginatedDocumentTypes = sortedDocumentTypes.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  // Passing this is what makes SortableTable skip its own per-page sort, while it still owns the
  // header arrow off `defaultOrderColumn` and the values it hands back here. Only a column
  // without `disableSort` can reach this, and each of those is named after a DocumentType field.
  const handleSortByColumn = (
    _event: React.MouseEvent,
    { id, order }: { id: keyof DocumentType; order: 'asc' | 'desc' }
  ) => {
    setSortColumn(id);
    setSortOrder(order);
    // Under a new order the row that was at the top of page 3 is no longer there.
    setPage(0);
  };

  // If filters shrink the list, snap the page back into range.
  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  const handleEditDocumentType = (documentType: DocumentType) => {
    setDocumentTypeToEdit(documentType);
  };

  const handleViewDocumentType = (documentType: DocumentType) => {
    setDocumentTypeToView(documentType);
  };

  const handleClose = () => {
    setDocumentTypeToEdit(null);
  };

  const handleCloseView = () => {
    setDocumentTypeToView(null);
  };

  const handleRequestDelete = (documentType: DocumentType) => {
    setDocumentTypeToDelete(documentType);
  };

  const handleConfirmDelete = async () => {
    if (!documentTypeToDelete) return;
    try {
      await axios.delete(
        `/api/v1/document_types/organizations/${organization.id}/document_types/${documentTypeToDelete.id}`
      );
      setDocumentTypeToDelete(null);
      await reloadDocumentTypes();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleEnableDocumentType = async (documentTypeId: number, newStatus: boolean) => {
    try {
      await axios.patch(
        `/api/v1/document_types/organizations/${organization.id}/document_types/${documentTypeId}/is_enabled`,
        {
          is_enabled: newStatus,
        }
      );
      await reloadDocumentTypes();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const getActionCell = (documentType: DocumentType) => {
    const isMandatory = documentType.type === 'mandatory';
    if (isMandatory) {
      const menuOptions = [
        {
          key: `view_${documentType.id}`,
          node: (
            <div className="flex items-center gap-8">
              <EyeIcon />
              View
            </div>
          ),
          onClick: () => handleViewDocumentType(documentType),
        },
      ];
      return <ThreeDotsMenu options={menuOptions} disableScrollLock />;
    }
    const menuOptions = [
      {
        key: `edit_${documentType.id}`,
        node: (
          <div className="flex items-center gap-8">
            <PencilIcon />
            Edit
          </div>
        ),
        onClick: () => handleEditDocumentType(documentType),
      },
      {
        key: `delete_${documentType.id}`,
        node: (
          <div className="flex items-center gap-8">
            <TrashIcon />
            Delete
          </div>
        ),
        onClick: () => handleRequestDelete(documentType),
      },
    ];
    return <ThreeDotsMenu options={menuOptions} disableScrollLock />;
  };

  const handleUpdateDocumentType = async (values: Record<string, unknown>) => {
    try {
      if (!documentTypeToEdit) {
        return;
      }

      await axios.put(
        `/api/v1/document_types/organizations/${organization.id}/document_types/${documentTypeToEdit.id}`,
        values
      );
      await reloadDocumentTypes();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const columns = [
    { id: 'display_name', label: 'Name' },
    { id: 'key', label: 'Key' },
    {
      id: 'lobs',
      label: 'Lines of Business',
      specialCell: ({ is_all_lobs, lobs = [] }: { is_all_lobs: boolean; lobs: string[] }) =>
        is_all_lobs ? (
          'All'
        ) : (
          <span>{lobs?.map((lob) => getLobDescription(lob, lobConfigurationsDict)).join(', ')}</span>
        ),
    },
    {
      id: 'last_updated_datetime',
      label: 'Last Update',
      specialCell: (row: DocumentType) =>
        row.last_updated_datetime ? serverDateTimeToLocal(row.last_updated_datetime) : '—',
    },
    {
      id: 'last_updated_by_username',
      label: 'Updated by',
      specialCell: (row: DocumentType) => row.last_updated_by_username || '—',
    },
    {
      id: 'type',
      label: 'Is Custom',
      specialCell: ({ type }: { type: string }) => (type === 'custom' ? <CheckIcon className="fill-slate-700" /> : ''),
    },
    {
      id: 'is_enabled',
      disableSort: true,
      label: 'Enable',
      specialCell: (documentType: DocumentType) => {
        // Server-side tiering: only the default types the system keeps writing are locked, and
        // only in the disable direction — matching the endpoint, which never rejects an enable.
        // A locked type can already sit at is_enabled=false (the PATCH accepted any key before
        // the guard existed) and this screen fetches unfiltered, so that row does render here;
        // leaving the switch locked would strand it off with no way back.
        const isDisableLocked = !documentType.can_disable && documentType.is_enabled;
        const switchEl = (
          <Switch
            checked={documentType.is_enabled}
            onChange={async (_, newStatus) => {
              if (isDisableLocked) return;
              await handleEnableDocumentType(documentType.id, newStatus);
            }}
            disabled={isDisableLocked}
          />
        );
        // Every locked row keeps an explanation: the one that is on says why it cannot be turned
        // off, the one that is off says the enable it still offers cannot be undone.
        const lockTooltip = documentType.can_disable
          ? null
          : isDisableLocked
            ? SYSTEM_DOC_TYPE_DISABLE_TOOLTIP
            : SYSTEM_DOC_TYPE_ENABLE_IS_FINAL_TOOLTIP;
        if (lockTooltip) {
          return (
            <FsTooltip title={lockTooltip} arrow>
              {switchEl}
            </FsTooltip>
          );
        }
        return switchEl;
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      specialCell: getActionCell,
    },
  ];

  return (
    <>
      <SortableTable
        columns={columns}
        rows={paginatedDocumentTypes}
        onSortByColumn={handleSortByColumn}
        defaultOrderColumn={columns.findIndex((column) => column.id === sortColumn)}
        paginationProps={{
          rowsPerPageOptions: [ROWS_PER_PAGE],
          count: sortedDocumentTypes.length,
          rowsPerPage: ROWS_PER_PAGE,
          page,
          onChangePage: (_: unknown, newPage: number) => setPage(newPage),
          onChangeRowsPerPage: undefined,
        }}
      />
      {documentTypeToEdit ? (
        <UpsertDocumentTypeDialog
          documentType={documentTypeToEdit}
          onSubmit={handleUpdateDocumentType}
          onClose={handleClose}
          documentTypesDict={documentTypesDict}
        />
      ) : null}
      {documentTypeToView ? (
        <ViewDocumentTypeDialog documentType={documentTypeToView} onClose={handleCloseView} />
      ) : null}
      <ConfirmModal
        isOpen={documentTypeToDelete !== null}
        onClose={() => setDocumentTypeToDelete(null)}
        onPrimaryBtnClick={handleConfirmDelete}
        title="Delete Document Type?"
        contentText={`Are you sure you want to delete document type "${documentTypeToDelete?.display_name ?? ''}"? This action cannot be undone.`}
        primaryButtonName="Delete"
        secondaryButtonName="Cancel"
      />
    </>
  );
};

export default DocumentTypesTable;
